import { useNavigate } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import type { ChatRecommendationDefinition } from "@/config/chatRecommendations";
import { navigateToChatEntryForApp } from "@/lib/chatNavigation";
import { cn } from "@/lib/utils";
import AIleafGold from "@/components/ui/AIleafGold.svg";
import { useAuth } from "@/hooks/useAuth";
import { deriveWorkspacePersona } from "@/config/workspacePersona";

type ChatRecommendationCardProps = {
  recommendation: ChatRecommendationDefinition;
  /** Optional override blurb (e.g. supplied by the server hint). */
  reason?: string;
  /** Hide the card without navigating. */
  onDismiss?: () => void;
  /** Optional analytics hook fired right before navigation. */
  onOpen?: (rec: ChatRecommendationDefinition) => void;
  className?: string;
};

/**
 * Inline card that surfaces below the latest assistant turn (alongside Quick
 * Answers) when the user's prompt looks like a fit for a structured tool or a
 * focused chat space. Non-blocking by design: the user can ignore it and keep
 * typing, or dismiss it explicitly.
 *
 * Visual language matches the chat surface: white card, gold/teal accent,
 * primary CTA on the right, dismiss in the corner.
 */
export const ChatRecommendationCard = ({
  recommendation,
  reason,
  onDismiss,
  onOpen,
  className,
}: ChatRecommendationCardProps) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const persona = deriveWorkspacePersona(userData);

  const handleOpen = () => {
    onOpen?.(recommendation);
    if (recommendation.kind === "tool" && recommendation.route) {
      navigate(recommendation.route);
      return;
    }
    if (recommendation.kind === "chat" && recommendation.chatEntry) {
      navigateToChatEntryForApp(navigate, recommendation.chatEntry, false, persona);
    }
  };

  return (
    <div
      className={cn(
        "mx-4 md:mx-8 mt-2 mb-1 rounded-2xl border border-[#C58C10]/60 bg-white shadow-sm",
        "px-3 py-2.5 md:px-4 md:py-3",
        className
      )}
      role="complementary"
      aria-label="Suggested next step"
    >
      <div className="flex items-start gap-3">
        {/* Gold Bodhi leaf — same brand mark used on the assistant avatar so the
            recommendation reads as a quiet aside from the same voice. */}
        <div
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            "bg-amber-50 ring-1 ring-[#C58C10]/40"
          )}
          aria-hidden
        >
          <img src={AIleafGold} alt="" width={20} height={20} className="h-5 w-5" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-2">
            <p className="font-inter text-[11px] font-medium uppercase tracking-wide text-stone-500">
              {recommendation.kind === "tool" ? "Suggested tool" : "Suggested chat space"}
            </p>
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss suggestion"
                className={cn(
                  "rounded-full p-1 text-stone-400 transition-colors",
                  "hover:bg-stone-100 hover:text-stone-600",
                  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bodhi-teal"
                )}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <p className="mt-0.5 font-literata text-sm font-semibold text-stone-800 md:text-base">
            {recommendation.label}
          </p>
          <p className="mt-0.5 font-inter text-xs leading-snug text-stone-600 md:text-sm">
            {reason || recommendation.blurb}
          </p>

          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="font-inter text-[11px] italic text-stone-400">
              Or keep chatting here — your call.
            </span>
            <button
              type="button"
              onClick={handleOpen}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full bg-bodhi-teal px-3 py-1.5",
                "font-inter text-xs font-medium text-white transition-colors",
                "hover:bg-bodhi-teal/90",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bodhi-teal"
              )}
            >
              <span>Open</span>
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
